import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";

import api from "libs/api";

const UsersSettingsContext = createContext();

const UsersSettingsProvider = ({ children }) => {
  const { t } = useTranslation();

  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { getUserData } = useApp();

  const [photo_progress, setPhotoProgress] = useState("0%");

  const {
    error,
    isLoading: loading,
    data,
  } = useQuery({
    queryKey: ["users-settings", id],
    queryFn: () => api.get(`/admin/users/${id}/settings`),
  });

  useEffect(() => {
    if (error) {
      navigate("/users");
      toast.error(t("User not found."));
    }
  }, [error, navigate, t]);

  const updateSettings = (values, successMessage) =>
    new Promise(async (resolve, reject) => {
      toast.dismiss();

      try {
        let data_to_send = { ...values };

        if (data_to_send?.settings?.email_notifications) {
          data_to_send = {
            settings: {
              email_notifications: {
                ...data?.settings?.email_notifications,
                ...values?.settings?.email_notifications,
              },
            },
          };
        }

        await api.put(`/admin/users/${id}/settings`, data_to_send);

        if (values?.type === "webmaster") {
          queryClient.invalidateQueries(["users-settings", id]);
          navigate(`/webmasters/${id}/account-settings`);
        } else {
          queryClient.setQueryData(["users-settings", id], (prev) => ({
            ...prev,
            ...data_to_send,
          }));
        }

        toast.success(successMessage || t("Saved successfully."));
        resolve();
      } catch (err) {
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const changePassword = (values) =>
    new Promise(async (resolve, reject) => {
      toast.dismiss();

      try {
        await api.put(`/admin/users/${id}/change-password`, values);

        queryClient.setQueryData(["users-settings", id], (prev) => ({
          ...prev,
          last_password_changed: new Date(),
        }));

        toast.success(t("Saved successfully."));
        resolve();
      } catch (err) {
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const updatePhoto = (file, photo_preview) => {
    setPhotoProgress("0%");

    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append("photo", file);

        await axios.put(
          `${process.env.REACT_APP_API}/admin/users/${id}/settings`,
          formData,
          {
            withCredentials: true,
            onUploadProgress: ({ loaded, total }) => {
              const percent = Math.floor((loaded * 100) / total);
              setPhotoProgress(`${percent}%`);
            },
          },
        );

        queryClient.setQueryData(["users-settings", id], (prev) => ({
          ...prev,
          photo: photo_preview,
        }));

        resolve();
        setPhotoProgress("100%");
      } catch (err) {
        toast.error(t("Something went wrong."));
        reject(err);
      }
    });
  };

  const deleteAccount = () =>
    new Promise(async (resolve, reject) => {
      try {
        await api.delete(`/admin/users/${id}`);
        await getUserData();

        navigate("/users");

        toast.success(t("Deleted successfully."));
        resolve();
      } catch (err) {
        toast.error(t("Something went wrong."));
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data,
      loading,
      error,
      photo_progress,
      updateSettings,
      changePassword,
      updatePhoto,
      deleteAccount,
    };
    // eslint-disable-next-line
  }, [data, photo_progress, loading, error]);

  return (
    <UsersSettingsContext.Provider value={value}>
      {children}
    </UsersSettingsContext.Provider>
  );
};

const useUsersSettings = () => useContext(UsersSettingsContext);
export { UsersSettingsContext, useUsersSettings };
export default UsersSettingsProvider;
